// No permissions needed
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Button, Card, CardContent, Divider, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useEffect, useMemo, useState } from 'react';
import { useGetOne, useRecordContext, useTranslate } from 'react-admin';

import {
  GetB2bCompaniesIdEndpoint,
  GetB2bDashboardMatchingContributionsData,
  GetB2bDashboardMatchingContributionsResponse,
} from '@Common/types/bo/b2b/companies';

import { mockMatchingContribution } from '@sh4res/bo-common/components/B2bCompanyDashboard/mock_data';
import {
  B2bCompanyDashboardProps,
  LegendLabel,
  PieCenterLabel,
} from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

const convertToDisplayFormat = (
  data: GetB2bDashboardMatchingContributionsData,
): Array<{
  year: number;
  paid: number;
  awaiting_company: number;
  not_distributed: number;
  max: number;
}> =>
  Object.entries(data)
    .map(([yearStr, yearData]) => ({
      year: parseInt(yearStr, 10),
      paid: yearData.alreadyPaid,
      awaiting_company: yearData.awaitingCompany,
      not_distributed: yearData.notDistributed,
      max: yearData.totalPotentialAmount,
    }))
    .sort((a, b) => a.year - b.year);

export const MatchingContributionBreakdownSection = ({
  useMock,
  resource,
}: B2bCompanyDashboardProps) => {
  const translate = useTranslate();
  const record = useRecordContext<GetB2bCompaniesIdEndpoint['Client']['ResponseBody']>();

  const { data: realData } = useGetOne<GetB2bDashboardMatchingContributionsResponse>(
    `${resource}/dashboard/matching_contributions`,
    { id: record?.id as number },
    { enabled: !!record },
  );

  const [year, setYear] = useState<number>(0);

  const data = useMemo(() => {
    if (useMock) {
      return convertToDisplayFormat(mockMatchingContribution);
    }

    if (!realData) {
      return null;
    }

    return convertToDisplayFormat(realData.data);
  }, [realData, useMock]);

  useEffect(() => {
    if (year === 0 && data) {
      setYear(data[data.length - 1].year);
    }
  }, [year, setYear, data]);
  const details = useMemo(() => data?.find((details) => details.year === year), [data, year]);

  // Hide the component if there's no data or if all years have 0 max amount
  if (!record || !data || !details || data.every((entry) => entry.max === 0)) {
    return null;
  }

  const pieData = [
    {
      id: 'paid',
      value: details.paid,
      label: translate(`resources.${resource}.dashboard.matching_contribution.paid`),
      color: '#594FF4',
    },
    {
      id: 'awaiting_company',
      value: details.awaiting_company,
      label: translate(`resources.${resource}.dashboard.matching_contribution.awaiting_company`),
      color: 'rgba(89, 79, 244, 0.2)',
    },
    {
      id: 'not_distributed',
      value: details.not_distributed,
      label: translate(`resources.${resource}.dashboard.matching_contribution.not_distributed`),
      color: 'rgba(248, 248, 248, 1)',
    },
  ];

  return (
    <Card sx={{ margin: '2px 2px 15px 2px', borderRadius: '8px' }}>
      <CardContent sx={{ padding: 0, paddingBottom: '0px !important' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 20px 15px 20px',
          }}>
          <Typography variant="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {translate(`resources.${resource}.dashboard.titles.matching_contribution`)}
          </Typography>
          <Box>
            {data.map((entry, idx) => (
              <Button
                key={idx}
                disableRipple
                data-testid="BoExternalB2bCompanyDashboardMatchingContributionBreakdownGraphs_YearButton"
                sx={{
                  color: entry.year === year ? 'black' : 'rgba(0, 0, 0, 0.6)',
                  backgroundColor: entry.year === year ? '#E8E8E8' : undefined,
                  borderRadius: '20px',
                  minWidth: 48,
                  marginRight: idx === data.length - 1 ? '0 !important' : '8px !important',
                  '&:hover': {
                    backgroundColor: entry.year === year ? '#E8E8E8' : undefined,
                  },
                  '&:focus-visible': {
                    outline: '1px solid',
                  },
                }}
                onClick={() => setYear(entry.year)}
                aria-pressed={entry.year === year}>
                {entry.year}
              </Button>
            ))}
          </Box>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 400, padding: '40px 60px' }}>
          <Box>
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 135,
                  valueFormatter: (entry) => formatCentsNumber(entry.value),
                },
              ]}
              height={350}
              width={400}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}>
              <PieCenterLabel
                total={formatCentsNumber(details.max)}
                label={`resources.${resource}.dashboard.matching_contribution.potential_payout`}
              />
            </PieChart>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
            role="list"
            aria-label={translate(
              `resources.${resource}.dashboard.matching_contribution.data_for_year`,
              { year },
            )}>
            {pieData.map((entry, idx) => (
              <Box
                key={idx}
                role="listitem"
                tabIndex={0}
                aria-label={`${entry.label}: ${formatCentsNumber(entry.value)}`}
                sx={{
                  '&:focus': {
                    outline: '1px solid',
                    outlineOffset: '2px',
                    borderRadius: '4px',
                  },
                }}>
                <LegendLabel
                  id={String(idx)}
                  color={entry.color}
                  label={entry.label}
                  value={formatCentsNumber(entry.value)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
